import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { BUS_AIRCRAFT_CODE } from '@fcom/booking/modules/ticket-selection/interfaces';
import { BoundType, Duration, LocationInfoMap } from '@fcom/dapi';
import {
  FinnairBoundItem,
  FinnairDisruptedBoundItem,
  FinnairItineraryItem,
  FinnairItineraryItemFlight,
  FinnairItineraryItemFlightStatus,
  FinnairItineraryItemLayover,
} from '@fcom/dapi/api/models';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { LocalizationPipe } from '@fcom/ui-translate';

import { EndPointChanges, FlightChanges } from '../../interfaces/index';
import { isFlight } from '../../utils/common-booking.utils';

@Component({
  selector: 'fin-itinerary-timeline',
  styleUrls: ['./itinerary.scss', 'arrow-block.scss'],
  templateUrl: './itinerary-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ItineraryTimelineComponent implements OnChanges {
  readonly BUS_AIRCRAFT_CODE = BUS_AIRCRAFT_CODE;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private finLocalization = inject(LocalizationPipe);

  /**
   * The translation prefix for strings that are not common to both departure and return flights.
   * E.g. "Departure" vs. "Return"
   */
  @Input()
  nonCommonTranslationPrefix: string;

  @Input()
  boundType: BoundType = null;

  @Input()
  bound: FinnairBoundItem | FinnairDisruptedBoundItem;

  @Input()
  locations: LocationInfoMap;

  @Input()
  isBookingFlow = false;

  @Input()
  title: string;

  @Input()
  legChanges: Map<string, FlightChanges> = new Map();

  @Input()
  departureChanges: EndPointChanges;

  @Input()
  arrivalChanges: EndPointChanges;

  @Input()
  boundChangeDuration: Duration;

  @Input()
  showNoChangesNotification: boolean;

  fareDetailMap = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bound']) {
      this.computeFareDetails();
    }
  }

  toType(t: FinnairItineraryItem): FinnairItineraryItemFlight | FinnairItineraryItemLayover {
    return 'technicalStops' in t ? (t as FinnairItineraryItemFlight) : (t as FinnairItineraryItemLayover);
  }

  computeFareDetails(): void {
    this.fareDetailMap =
      this.bound?.itinerary?.filter(isFlight).reduce((map, item) => {
        const flightLeg = this.toType(item) as FinnairItineraryItemFlight;
        const isUpgraded = flightLeg.upgradeItem?.status === FinnairItineraryItemFlightStatus.CONFIRMED;
        if (!flightLeg.cabinClass && !flightLeg.bookingClass) {
          map.set(flightLeg.id, undefined);
        } else {
          const detail =
            isUpgraded || !this.bound?.fareFamily?.name
              ? this.finLocalization.transform('travelClass.' + flightLeg.cabinClass?.toLowerCase()) +
                (flightLeg.bookingClass ? ' ' + flightLeg.bookingClass : '')
              : this.bound.fareFamily.name + (flightLeg.bookingClass ? ` (${flightLeg.bookingClass})` : '');

          map.set(flightLeg.id, detail);
        }
        return map;
      }, new Map<string, string>()) ?? new Map();
  }
}
